// The waitlist wall's approval poll (plan 102 slice 05, decision 8): an approved
// person continues without a manual reload.
//
// BOUNDED: one probe per `intervalMs` (default {@link APPROVAL_POLL_MS}) while the tab
// is VISIBLE — a hidden tab asks nothing, so a wall left open in a background tab for
// a week is not a week of `/v1/me` reads. Focus and the tab becoming visible ask at
// once, which is the moment the person is looking (the probe runs outside the query
// cache, so `query.ts`'s `refetchOnWindowFocus` never reaches it; the wall says it here). One probe in flight
// at a time.
//
// STOPS on approval (the first probe that answers it, then never again) and on the
// owner's disposal (`onCleanup`: the interval and both listeners). No retry ladder of
// its own: a failed probe is just a missed tick, and the next tick is the retry.
//
// `check` answers "approved?"; `onApproved` runs once. Neither is the shared
// `session_me` query: the caller probes outside the cache and moves the URL BEFORE it
// re-reads the gate's query, so the app that mounts on approval mounts on the funnel
// rather than on whatever path the wall happened to sit at.

import { onCleanup } from 'solid-js';

/** How often the wall asks while the tab is visible. */
export const APPROVAL_POLL_MS = 15_000;

/** Run `tick` every `ms`; answer the cancel. The real one is `setInterval`; tests drive it. */
export type PollSchedule = (tick: () => void, ms: number) => () => void;

const intervalSchedule: PollSchedule = (tick, ms) => {
  const timer = setInterval(tick, ms);
  return () => clearInterval(timer);
};

export function createApprovalPoll(input: {
  readonly check: () => Promise<boolean>;
  readonly onApproved: () => void;
  /** The tick seam; `setInterval` at {@link APPROVAL_POLL_MS} unless a test hands one. */
  readonly schedule?: PollSchedule;
}): void {
  let done = false;
  let inFlight = false;

  const probe = async (): Promise<void> => {
    if (done || inFlight || document.visibilityState === 'hidden') return;
    inFlight = true;
    try {
      if ((await input.check()) && !done) {
        stop();
        input.onApproved();
      }
    } catch {
      // A missed tick: the next one is the retry (header).
    } finally {
      inFlight = false;
    }
  };

  const onFocus = (): void => void probe();
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') void probe();
  };
  const cancel = (input.schedule ?? intervalSchedule)(() => void probe(), APPROVAL_POLL_MS);
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisible);

  function stop(): void {
    done = true;
    cancel();
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisible);
  }
  onCleanup(stop);
}
